/**
 * The sixteen animals — one per seat in a full room.
 *
 * All built from the same grammar: a fat rounded head on a 100×100 canvas,
 * ink outline, flat Okabe-Ito fills. The silhouette — ears, beak, crest,
 * trunk — carries the identity, so two animals never differ by colour alone
 * and the set survives every kind of colour blindness by construction.
 */
const INK = "#12162c";
const CARD = "#fdfdff";

type Draw = { fill: string; shapes: React.ReactNode };

const eyes = (
  <>
    <circle cx="38" cy="52" r="5.5" fill={INK} />
    <circle cx="62" cy="52" r="5.5" fill={INK} />
    <circle cx="39.8" cy="50" r="1.8" fill={CARD} />
    <circle cx="63.8" cy="50" r="1.8" fill={CARD} />
  </>
);

const head = (fill: string) => (
  <circle cx="50" cy="55" r="32" fill={fill} stroke={INK} strokeWidth="5" />
);

const ANIMALS: Record<string, Draw> = {
  parrot: {
    fill: "#009e73",
    shapes: (
      <>
        <path d="M50 25c-6-2-9-8-7-14 3 1 5 3 6 6 1-3 3-5 6-6 2 6-1 12-5 14z" fill="#d55e00" stroke={INK} strokeWidth="4.5" strokeLinejoin="round" />
        {head("#009e73")}
        {eyes}
        <path d="M50 58l-7 9c4 3 10 3 14 0z" fill="#e69f00" stroke={INK} strokeWidth="4.5" strokeLinejoin="round" />
      </>
    ),
  },
  cat: {
    fill: "#cc79a7",
    shapes: (
      <>
        <path d="M26 36 22 14l18 10z" fill="#cc79a7" stroke={INK} strokeWidth="5" strokeLinejoin="round" />
        <path d="M74 36 78 14 60 24z" fill="#cc79a7" stroke={INK} strokeWidth="5" strokeLinejoin="round" />
        {head("#cc79a7")}
        {eyes}
        <path d="M46 62h8l-4 5z" fill={INK} />
        <path d="M50 67v6M40 70c3 3 7 4 10 3M60 70c-3 3-7 4-10 3" stroke={INK} strokeWidth="4" strokeLinecap="round" fill="none" />
      </>
    ),
  },
  frog: {
    fill: "#009e73",
    shapes: (
      <>
        <circle cx="34" cy="28" r="11" fill="#009e73" stroke={INK} strokeWidth="5" />
        <circle cx="66" cy="28" r="11" fill="#009e73" stroke={INK} strokeWidth="5" />
        <circle cx="34" cy="28" r="4" fill={INK} />
        <circle cx="66" cy="28" r="4" fill={INK} />
        {head("#009e73")}
        <path d="M36 64c9 6 19 6 28 0" stroke={INK} strokeWidth="5" strokeLinecap="round" fill="none" />
      </>
    ),
  },
  owl: {
    fill: "#0072b2",
    shapes: (
      <>
        <path d="M28 30 24 16l14 6zM72 30l4-14-14 6z" fill="#0072b2" stroke={INK} strokeWidth="5" strokeLinejoin="round" />
        {head("#0072b2")}
        <circle cx="38" cy="52" r="11" fill={CARD} stroke={INK} strokeWidth="4" />
        <circle cx="62" cy="52" r="11" fill={CARD} stroke={INK} strokeWidth="4" />
        <circle cx="38" cy="52" r="4.5" fill={INK} />
        <circle cx="62" cy="52" r="4.5" fill={INK} />
        <path d="M50 58l-5 8h10z" fill="#e69f00" stroke={INK} strokeWidth="4" strokeLinejoin="round" />
      </>
    ),
  },
  pig: {
    fill: "#cc79a7",
    shapes: (
      <>
        <path d="M28 32c-4-6-2-12 3-14 2 4 6 6 10 6zM72 32c4-6 2-12-3-14-2 4-6 6-10 6z" fill="#cc79a7" stroke={INK} strokeWidth="5" strokeLinejoin="round" />
        {head("#cc79a7")}
        {eyes}
        <ellipse cx="50" cy="66" rx="11" ry="8" fill="#e3aecb" stroke={INK} strokeWidth="4" />
        <circle cx="46" cy="66" r="2" fill={INK} />
        <circle cx="54" cy="66" r="2" fill={INK} />
      </>
    ),
  },
  fox: {
    fill: "#d55e00",
    shapes: (
      <>
        <path d="M24 40 18 14l20 12zM76 40l6-26-20 12z" fill="#d55e00" stroke={INK} strokeWidth="5" strokeLinejoin="round" />
        {head("#d55e00")}
        <path d="M50 87c-12 0-20-8-22-18l22 6 22-6c-2 10-10 18-22 18z" fill={CARD} stroke={INK} strokeWidth="4" strokeLinejoin="round" />
        {eyes}
        <path d="M46 66h8l-4 5z" fill={INK} />
      </>
    ),
  },
  chick: {
    fill: "#f0e442",
    shapes: (
      <>
        <path d="M46 22c-1-6 3-10 8-10-2 4-1 7 2 9z" fill="#e69f00" stroke={INK} strokeWidth="4" strokeLinejoin="round" />
        {head("#f0e442")}
        {eyes}
        <path d="M50 58l-6 7h12z" fill="#e69f00" stroke={INK} strokeWidth="4" strokeLinejoin="round" />
      </>
    ),
  },
  panda: {
    fill: "#fbfcfe",
    shapes: (
      <>
        <circle cx="28" cy="30" r="10" fill={INK} />
        <circle cx="72" cy="30" r="10" fill={INK} />
        {head("#fbfcfe")}
        <ellipse cx="38" cy="52" rx="9" ry="11" fill={INK} />
        <ellipse cx="62" cy="52" rx="9" ry="11" fill={INK} />
        <circle cx="38" cy="50" r="3.5" fill={CARD} />
        <circle cx="62" cy="50" r="3.5" fill={CARD} />
        <path d="M46 66h8l-4 5z" fill={INK} />
      </>
    ),
  },
  bunny: {
    fill: "#e3aecb",
    shapes: (
      <>
        <ellipse cx="38" cy="18" rx="8" ry="16" fill="#e3aecb" stroke={INK} strokeWidth="5" />
        <ellipse cx="62" cy="18" rx="8" ry="16" fill="#e3aecb" stroke={INK} strokeWidth="5" />
        {head("#e3aecb")}
        {eyes}
        <path d="M46 62h8l-4 5z" fill={INK} />
        <path d="M50 67v5M44 74c2 2 4 2 6 1M56 74c-2 2-4 2-6 1" stroke={INK} strokeWidth="3.5" strokeLinecap="round" fill="none" />
      </>
    ),
  },
  bear: {
    fill: "#b57b00",
    shapes: (
      <>
        <circle cx="28" cy="30" r="10" fill="#b57b00" stroke={INK} strokeWidth="5" />
        <circle cx="72" cy="30" r="10" fill="#b57b00" stroke={INK} strokeWidth="5" />
        {head("#b57b00")}
        {eyes}
        <ellipse cx="50" cy="66" rx="12" ry="9" fill="#f0c85c" stroke={INK} strokeWidth="4" />
        <path d="M46 64h8l-4 5z" fill={INK} />
      </>
    ),
  },
  penguin: {
    fill: "#12162c",
    shapes: (
      <>
        <circle cx="50" cy="55" r="32" fill="#12162c" stroke={INK} strokeWidth="5" />
        <ellipse cx="50" cy="62" rx="20" ry="22" fill={CARD} />
        <circle cx="38" cy="48" r="5.5" fill={INK} />
        <circle cx="62" cy="48" r="5.5" fill={INK} />
        <circle cx="39.8" cy="46" r="1.8" fill={CARD} />
        <circle cx="63.8" cy="46" r="1.8" fill={CARD} />
        <path d="M50 54l-6 7h12z" fill="#e69f00" stroke={INK} strokeWidth="4" strokeLinejoin="round" />
      </>
    ),
  },
  mouse: {
    fill: "#e3aecb",
    shapes: (
      <>
        <circle cx="28" cy="28" r="13" fill="#e3aecb" stroke={INK} strokeWidth="5" />
        <circle cx="72" cy="28" r="13" fill="#e3aecb" stroke={INK} strokeWidth="5" />
        <circle cx="28" cy="28" r="6" fill="#cc79a7" />
        <circle cx="72" cy="28" r="6" fill="#cc79a7" />
        {head("#e3aecb")}
        {eyes}
        <circle cx="50" cy="64" r="4" fill={INK} />
      </>
    ),
  },
  koala: {
    fill: "#7c8399",
    shapes: (
      <>
        <circle cx="26" cy="34" r="14" fill="#7c8399" stroke={INK} strokeWidth="5" />
        <circle cx="74" cy="34" r="14" fill="#7c8399" stroke={INK} strokeWidth="5" />
        <circle cx="26" cy="34" r="6" fill="#e3aecb" />
        <circle cx="74" cy="34" r="6" fill="#e3aecb" />
        {head("#7c8399")}
        {eyes}
        <ellipse cx="50" cy="62" rx="8" ry="11" fill={INK} />
      </>
    ),
  },
  tiger: {
    fill: "#e69f00",
    shapes: (
      <>
        <circle cx="30" cy="30" r="9" fill="#e69f00" stroke={INK} strokeWidth="5" />
        <circle cx="70" cy="30" r="9" fill="#e69f00" stroke={INK} strokeWidth="5" />
        {head("#e69f00")}
        <path d="M50 24v10M34 30l6 8M66 30l-6 8" stroke={INK} strokeWidth="5" strokeLinecap="round" />
        {eyes}
        <path d="M46 64h8l-4 5z" fill={INK} />
        <path d="M50 69v4M42 74c3 2 6 3 8 2M58 74c-3 2-6 3-8 2" stroke={INK} strokeWidth="3.5" strokeLinecap="round" fill="none" />
      </>
    ),
  },
  whale: {
    fill: "#56b4e9",
    shapes: (
      <>
        <path d="M46 20c0-6 4-10 9-10-2 3-2 6 0 8 2-2 5-3 8-2-2 4-6 7-11 7z" fill="#56b4e9" stroke={INK} strokeWidth="4" strokeLinejoin="round" />
        {head("#56b4e9")}
        {eyes}
        <path d="M40 64c6 5 14 5 20 0" stroke={INK} strokeWidth="4.5" strokeLinecap="round" fill="none" />
      </>
    ),
  },
  bee: {
    fill: "#f0e442",
    shapes: (
      <>
        <path d="M34 22 28 10M66 22l6-12" stroke={INK} strokeWidth="4" strokeLinecap="round" />
        <circle cx="28" cy="9" r="4" fill={INK} />
        <circle cx="72" cy="7" r="4" fill={INK} />
        {head("#f0e442")}
        <path d="M22 44h56M26 66h48" stroke={INK} strokeWidth="7" strokeLinecap="round" />
        {eyes}
      </>
    ),
  },
};

export const ANIMAL_IDS = Object.keys(ANIMALS);

export function Animal({ id, className = "" }: { id: string; className?: string }) {
  const animal = ANIMALS[id] ?? ANIMALS.parrot;

  return (
    <svg viewBox="0 0 100 100" className={className} role="img" aria-label={id}>
      {animal.shapes}
    </svg>
  );
}
